import { useState } from 'react';
import { ChevronDown, ChevronUp, Search, HelpCircle } from 'lucide-react';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const faqs = [
    {
      category: 'Using AutoSearchSA',
      questions: [
        {
          q: 'What is AutoSearchSA?',
          a: 'AutoSearchSA is a car search aggregator that pulls listings from multiple South African dealers and platforms into one place. Instead of visiting WeBuyCars, AutoTrader, Cars.co.za, and other sites separately, you can search them all at once here.'
        },
        {
          q: 'Is AutoSearchSA free to use?',
          a: 'Yes! Searching for cars on AutoSearchSA is completely free. We make money through referral partnerships with finance, insurance, and inspection providers – not from you.'
        },
        {
          q: 'Where do the car listings come from?',
          a: 'We aggregate listings from major SA dealers and platforms including WeBuyCars, AutoTrader, Weelee, Cars.co.za, and various independent dealerships. Our database is updated regularly to show the latest stock.'
        },
        {
          q: 'Can I buy a car directly through AutoSearchSA?',
          a: 'We don\'t sell cars directly. When you find a car you like, you\'ll be directed to the dealer\'s website or contact details. We help you find and compare – the actual purchase happens with the dealer.'
        }
      ]
    },
    {
      category: 'Vehicle Inspections',
      questions: [
        {
          q: 'Why should I get an inspection before buying?',
          a: 'Statistics show that 40% of used cars have hidden defects. An independent inspection can reveal mechanical issues, accident damage, and odometer fraud that might not be visible during a test drive. It\'s a small investment that can save you thousands in unexpected repairs.'
        },
        {
          q: 'How much does an inspection cost?',
          a: 'Inspections range from R850 for a basic check to R2,200 for a comprehensive premium inspection. The exact price depends on the level of detail and the inspection provider in your area.'
        },
        {
          q: 'Who performs the inspections?',
          a: 'We partner with certified inspection services like Screan.co.za who have qualified mechanics throughout South Africa. Inspectors are independent and don\'t have any relationship with the sellers.'
        },
        {
          q: 'Can I get an inspection for a car from a private seller?',
          a: 'Absolutely! In fact, inspections are especially important for private sales where there\'s less recourse if something goes wrong. The inspector can come to wherever the car is located.'
        }
      ]
    },
    {
      category: 'Finance',
      questions: [
        {
          q: 'How does vehicle finance work?',
          a: 'Vehicle finance is a loan specifically for buying a car. You pay a deposit (optional), then monthly installments over 24-84 months. The car serves as security for the loan. Interest rates in SA typically range from 11-15% depending on your credit profile.'
        },
        {
          q: 'Can I get finance with bad credit?',
          a: 'It\'s more difficult but not impossible. Some finance houses specialize in clients with impaired credit. You may need a larger deposit or accept higher interest rates. Fill out our form and a consultant can advise on your specific situation.'
        },
        {
          q: 'What is a balloon payment?',
          a: 'A balloon payment is a lump sum due at the end of your finance term. It reduces your monthly payments but means you\'ll owe a large amount at the end. You can pay it, refinance it, or trade in the car. It\'s optional and typically ranges from 0-40% of the car\'s value.'
        },
        {
          q: 'How much deposit do I need?',
          a: 'While some finance houses offer 100% finance (no deposit), having a deposit improves your approval chances and reduces monthly payments. Aim for 10-20% if possible. A larger deposit also means paying less interest overall.'
        }
      ]
    },
    {
      category: 'Insurance',
      questions: [
        {
          q: 'Is car insurance mandatory in South Africa?',
          a: 'Third-party liability insurance is not legally required in SA, but it\'s highly recommended. If you\'re financing a car, comprehensive insurance is usually required by the finance house. Given SA\'s high accident and theft rates, driving uninsured is a significant risk.'
        },
        {
          q: 'What\'s the difference between comprehensive and third-party insurance?',
          a: 'Comprehensive covers your car for theft, accidents, fire, and damage, plus third-party liability. Third-party only covers damage you cause to others\' property – if your car is stolen or damaged, you get nothing. For cars worth over R100,000, comprehensive usually makes sense.'
        },
        {
          q: 'How much does car insurance cost?',
          a: 'Premiums vary widely based on your car\'s value, your age, driving history, and where you live. As a rough guide, expect to pay 2-3% of your car\'s value annually for comprehensive cover. A R300,000 car might cost R500-800/month to insure.'
        },
        {
          q: 'What is excess and how does it work?',
          a: 'Excess is the amount you pay out of pocket when making a claim. Standard excess is typically R2,500-5,000. There may be additional excess for young drivers, certain areas, or specific claim types. Higher excess = lower premiums.'
        }
      ]
    },
    {
      category: 'Life Cover & Income Protection',
      questions: [
        {
          q: 'Why do you talk about life cover on a car website?',
          a: 'Because most people insure their cars but not their ability to earn money. Car accidents are the #1 cause of disability claims in SA. If you can\'t work due to an accident, your car insurance won\'t pay your bond or feed your family. Income protection does.'
        },
        {
          q: 'What is income protection insurance?',
          a: 'Income protection pays you a monthly income if you can\'t work due to illness or injury. Unlike disability cover (which is a lump sum), it replaces your salary while you recover. It ensures you can still pay bills during a period when you have no income.'
        },
        {
          q: 'How much life cover do I need?',
          a: 'A common guideline is 10x your annual salary. Consider your debts (bond, car), dependents, and how long you\'d want to provide for your family. A R500,000 bond + 5 years of R30,000/month expenses = R2.3 million minimum.'
        },
        {
          q: 'Is life cover expensive?',
          a: 'It\'s more affordable than most people think, especially when young and healthy. A 30-year-old non-smoker might pay R200-400/month for R1 million in cover. The younger you start, the cheaper it is.'
        }
      ]
    }
  ];

  const filteredFaqs = faqs.map(category => ({
    ...category,
    questions: category.questions.filter(
      q => q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
           q.a.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  const handleToggle = (idx) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  let globalIndex = 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-800 to-gray-900 text-white py-16">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <HelpCircle className="w-16 h-16 mx-auto mb-4 text-blue-400" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Frequently Asked Questions</h1>
            <p className="text-xl text-gray-300">
              Everything you need to know about buying a car in South Africa
            </p>
          </div>
        </div>
      </section>

      {/* Search */}
      <div className="container py-8">
        <div className="max-w-2xl mx-auto">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl text-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            />
          </div>
        </div>
      </div>

      {/* FAQ Content */}
      <div className="container pb-16">
        <div className="max-w-3xl mx-auto">
          {filteredFaqs.map((category, catIdx) => (
            <div key={catIdx} className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-sm font-bold">
                  {category.questions.length}
                </span>
                {category.category}
              </h2>
              
              <div className="space-y-3">
                {category.questions.map((faq, qIdx) => {
                  const currentIndex = globalIndex++;
                  const isOpen = openIndex === currentIndex;
                  
                  return (
                    <div key={qIdx} className="bg-white rounded-xl shadow-sm overflow-hidden">
                      <button
                        onClick={() => handleToggle(currentIndex)}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition"
                      >
                        <span className="font-semibold text-gray-900 pr-4">{faq.q}</span>
                        {isOpen ? (
                          <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        )}
                      </button>
                      {isOpen && (
                        <div className="px-6 pb-4">
                          <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {filteredFaqs.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No questions found matching "{searchQuery}"</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FAQ;
